import { createContext, useContext, useMemo, useState, type ReactNode } from 'react'
import {
  type Locale,
  readLocale,
  translate,
  writeLocale,
} from '../lib/i18n'

type Vars = Record<string, string | number>

interface LocaleContextValue {
  locale: Locale
  setLocale: (locale: Locale) => void
  t: (key: string, vars?: Vars) => string
}

const LocaleContext = createContext<LocaleContextValue | null>(null)

export function LocaleProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(() => readLocale())

  const value = useMemo<LocaleContextValue>(() => {
    const setLocale = (next: Locale) => {
      writeLocale(next)
      setLocaleState(next)
    }
    return {
      locale,
      setLocale,
      t: (key, vars) => translate(locale, key, vars),
    }
  }, [locale])

  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>
}

export function useLocale(): LocaleContextValue {
  const ctx = useContext(LocaleContext)
  if (ctx) return ctx
  const locale = readLocale()
  return {
    locale,
    setLocale: writeLocale,
    t: (key, vars) => translate(locale, key, vars),
  }
}
